import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Container, Modal } from '../layout';
import SimpleTable from '../layout/table/SimpleTable';
import { SearchBar } from '../layout/inputs/SearchBar';
import { Select } from '../layout/inputs/Select';
import { createClient, deleteClient, getAllClients, updateClient } from '../../utils/clients';
import AugAlert from '../layout/alert/AugAlert';
import { Button as AugButton } from '../layout/buttons/Button';
import { useNavigate } from 'react-router-dom';
import Icon from '../layout/icons/Icons';
import styles from './ClientsTable.module.css';
import stylesAnalytics from './Analytics.module.css';

const ClientsTable = () => {
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [deletionLoading, setDeletionLoading] = useState(false);
    const [deletionError, setDeletionError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingLoading, setCreatingLoading] = useState(false);
    const [creatingError, setCreatingError] = useState('');
    const [createDisplayName, setCreateDisplayName] = useState('');
    const [createOrganization, setCreateOrganization] = useState('');
    const [createIndustry, setCreateIndustry] = useState('');
    const [createJobTitle, setCreateJobTitle] = useState('');

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingLoading, setEditingLoading] = useState(false);
    const [editingError, setEditingError] = useState('');
    const [editDisplayName, setEditDisplayName] = useState('');
    const [editOrganization, setEditOrganization] = useState('');
    const [editIndustry, setEditIndustry] = useState('');
    const [editJobTitle, setEditJobTitle] = useState('');
    const [reloadToken, setReloadToken] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [clients, setClients] = useState([]);
    const [clientsCount, setClientsCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    useEffect(() => {
        let isCancelled = false;
        const controller = new AbortController();
        const signal = controller.signal;

        const load = async () => {
            try {
                setLoading(true);
                setError('');
                // TODO: Implement search on backend if needed; for now we fetch all and filter client-side
                const data = await getAllClients({ page, pageSize, isUser: true });
                if (!isCancelled) {
                    let filteredClients = Array.isArray(data.clients) ? data.clients : [];
                    if (searchTerm && searchTerm.trim().length > 0) {
                        const lower = searchTerm.trim().toLowerCase();
                        filteredClients = filteredClients.filter(c =>
                            (c.displayName && c.displayName.toLowerCase().includes(lower)) ||
                            (c.clientId && c.clientId.toLowerCase().includes(lower)) ||
                            (c.organization && c.organization.toLowerCase().includes(lower))
                        );
                    }
                    setClients(filteredClients);
                    setClientsCount(data.totalCount);
                }
            } catch (e) {
                if (!isCancelled) setError(e?.message || 'Failed to load clients');
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };

        // debounce
        const t = setTimeout(load, searchTerm ? 350 : 0);
        return () => {
            isCancelled = true;
            controller.abort();
            clearTimeout(t);
        };
    }, [page, pageSize, searchTerm, reloadToken]);

    // Sync pageSize with the per-page select (dateRange here acts as page size selector)
    useEffect(() => {
        const n = parseInt(pageSize, 10);
        if (!Number.isNaN(n)) setPageSize(n);
    }, [pageSize]);

    // Reset to first page when search term changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const hasNext = clients.length === pageSize; // heuristic without totalCount

    const renderCell = (val) => {
        if (val == null) return '—';
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
        // Avoid rendering objects/arrays directly
        try {
            return JSON.stringify(val);
        } catch {
            return String(val);
        }
    };

    const renderRows = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={6}>Loading clients…</td>
                </tr>
            );
        }
        if (!clients || clients.length === 0) {
            return (
                <tr>
                    <td colSpan={6}>No clients found</td>
                </tr>
            );
        }

        return clients.map((c) => (
            <tr key={c.clientId || c.id || JSON.stringify(c)} onClick={() => handleRowClick(c)} style={{ cursor: 'pointer' }}>
                <td>{renderCell(c.displayName)}</td>
                <td>{renderCell(c.clientId)}</td>
                <td>{renderCell(c.organization)}</td>
                <td>{renderCell(c.industry)}</td>
                <td>{renderCell(c.jobTitle)}</td>
                <td>{c.timestamp ? new Date(c.timestamp).toLocaleString() : '—'}</td>
            </tr>
        ));
    };

    const renderMobileCards = () => {
        if (loading) {
            return <div className={styles.mobileEmptyState}>Loading clients…</div>;
        }

        if (!clients || clients.length === 0) {
            return <div className={styles.mobileEmptyState}>No clients found</div>;
        }

        return clients.map((c) => (
            <div
                key={c.clientId || c.id || JSON.stringify(c)}
                className={styles.mobileClientCard}
                role="button"
                tabIndex={0}
                onClick={() => handleRowClick(c)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(c);
                    }
                }}
            >
                <div className={styles.mobileCardHeader}>
                    <div className={styles.mobileCardTitleBlock}>
                        <div className={styles.mobileCardTitle}>{renderCell(c.displayName) || '—'}</div>
                        <div className={styles.mobileCardId}>{renderCell(c.clientId)}</div>
                    </div>
                </div>

                <div className={styles.mobileCardDetails}>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Organization</span>
                        <span className={styles.mobileDetailValue}>{renderCell(c.organization)}</span>
                    </div>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Industry</span>
                        <span className={styles.mobileDetailValue}>{renderCell(c.industry)}</span>
                    </div>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Job Title</span>
                        <span className={styles.mobileDetailValue}>{renderCell(c.jobTitle)}</span>
                    </div>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Created</span>
                        <span className={styles.mobileDetailValue}>{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '—'}</span>
                    </div>
                </div>

                <div className={styles.mobileCardActions}>
                    <button
                        type="button"
                        className={styles.mobileActionButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(c);
                        }}
                    >
                        View Details
                    </button>
                </div>
            </div>
        ));
    };

    const onPageChange = (nextPage) => {
        if (nextPage < 1) return;
        if (loading) return;
        setPage(nextPage);
    };
    const onPrev = () => onPageChange(page - 1);
    const onNext = () => onPageChange(page + 1);

    const handleRowClick = (client) => {
        setSelectedClient(client);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedClient(null);
    };

    const closeAllModals = () => {
        setShowModal(false);
        setShowDeleteConfirm(false);
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedClient(null);
        setCreatingError('');
        setEditingError('');
        setDeletionError('');
    };

    const openCreateModal = () => {
        setCreatingError('');
        setCreateDisplayName('');
        setCreateOrganization('');
        setCreateIndustry('');
        setCreateJobTitle('');
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        if (creatingLoading) return;
        setShowCreateModal(false);
        setCreatingError('');
    };

    const openEditModal = () => {
        if (!selectedClient) return;
        setEditingError('');
        setEditDisplayName(selectedClient.displayName || '');
        setEditOrganization(selectedClient.organization || '');
        setEditIndustry(selectedClient.industry || '');
        setEditJobTitle(selectedClient.jobTitle || '');
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (editingLoading) return;
        setShowEditModal(false);
        setEditingError('');
    };

    const handleUpdateClient = async () => {
        if (!selectedClient?.clientId) return;
        try {
            setEditingLoading(true);
            setEditingError('');

            const displayName = (editDisplayName || '').trim();
            const organization = (editOrganization || '').trim();
            const industry = (editIndustry || '').trim();
            const jobTitle = (editJobTitle || '').trim();

            if (!displayName) {
                setEditingError('Display Name is required');
                return;
            }

            await updateClient({
                clientId: selectedClient.clientId,
                body: {
                    displayName,
                    organization,
                    industry,
                    jobTitle,
                },
            });

            closeAllModals();
            setReloadToken((t) => t + 1);
        } catch (e) {
            setEditingError(e?.message || 'Failed to update client');
        } finally {
            setEditingLoading(false);
        }
    };

    const handleCreateClient = async () => {
        try {
            setCreatingLoading(true);
            setCreatingError('');

            const displayName = (createDisplayName || '').trim();
            const organization = (createOrganization || '').trim();
            const industry = (createIndustry || '').trim();
            const jobTitle = (createJobTitle || '').trim();

            if (!displayName) {
                setCreatingError('Display Name is required');
                return;
            }

            const clientId = crypto.randomUUID();

            await createClient({
                clientId,
                body: {
                    clientId,
                    displayName,
                    organization,
                    industry,
                    jobTitle,
                    isUser: false
                },
            });

            closeAllModals();
            setReloadToken((t) => t + 1);
            setPage(1);
        } catch (e) {
            setCreatingError(e?.message || 'Failed to create client');
        } finally {
            setCreatingLoading(false);
        }
    };

    const handleDeleteClient = async () => {
        if (!selectedClient) return;
        try {
            setDeletionLoading(true);
            setDeletionError('');
            await deleteClient({ clientId: selectedClient.clientId });
            // Refresh data
            setPage(1);
            setShowDeleteConfirm(false);
            handleCloseModal();
            setReloadToken((t) => t + 1);
        } catch (e) {
            setDeletionError(e?.message || 'Failed to delete client');
        } finally {
            setDeletionLoading(false);
        }
    };

    const columns = [
        { key: 'displayName', label: 'Display Name', sortable: true },
        { key: 'clientId', label: 'Client ID', sortable: true },
        { key: 'organization', label: 'Organization', sortable: true },
        { key: 'industry', label: 'Industry', sortable: true },
        { key: 'jobTitle', label: 'Job Title', sortable: true },
        { key: 'timestamp', label: 'Created', sortable: true, render: (val) => val ? new Date(val).toLocaleString() : '—' },
    ];

    return (
        <Container className={styles.dataContainer}>
            {error && (
                <AugAlert type="error" title="Error Alert" text={error} className="mb-4" />
            )}
            <div className={styles.clientsCard}>
                {loading ? 'Loading clients…' : `${clientsCount} Clients`}
                <AugButton onClick={openCreateModal}>
                    <Icon name="plus" /> Add Client
                </AugButton>
            </div>

            <div className="chat-filters">
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <SearchBar
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search clients..."
                                disabled={loading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3} className="ms-auto d-flex justify-content-end">
                        <Form.Group>
                            <Select
                                value={pageSize}
                                onChange={(e) => setPageSize(e.target.value)}
                                disabled={loading}
                            >
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </Select>
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            <Row>
                <Col>
                    <div className={styles.desktopTableWrap}>
                        <SimpleTable
                            className="clients-table"
                            headers={columns.map((c) => c.label)}
                            pagination={{
                                page,
                                totalPages: Math.ceil(clientsCount / pageSize) || 1,
                                hasNext,
                                loading,
                                onPageChange,
                                onPrev,
                                onNext
                            }}
                        >
                            {renderRows()}
                        </SimpleTable>
                    </div>
                    <div className={styles.mobileCardsWrap}>
                        {renderMobileCards()}
                    </div>
                </Col>
            </Row>

            {/* Client Details Modal */}
            <Modal
                open={showModal}
                onClose={handleCloseModal}
                title="Client Details"
                footer={
                    <>
                        <AugButton variant="outline" onClick={handleCloseModal}>
                            Close
                        </AugButton>
                        <AugButton
                            variant="outline-primary"
                            onClick={openEditModal}
                        >
                            Edit
                        </AugButton>
                        <AugButton color="var(--brand-as-purple-700)" onClick={() => setShowDeleteConfirm(true)}>
                            Delete
                        </AugButton>
                    </>
                }
            >
                {selectedClient && (
                    <Row>
                        <Col md={6}>
                            <p><strong>Display Name:</strong> {renderCell(selectedClient.displayName)}</p>
                            <p><strong>Client ID:</strong> {renderCell(selectedClient.clientId)}</p>
                            <p><strong>Organization:</strong> {renderCell(selectedClient.organization)}</p>
                            <p><strong>Industry:</strong> {renderCell(selectedClient.industry)}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Job Title:</strong> {renderCell(selectedClient.jobTitle)}</p>
                            <p><strong>Created:</strong> {selectedClient.timestamp ? new Date(selectedClient.timestamp).toLocaleString() : '—'}</p>
                            <p><strong>Tutorial Complete:</strong> {renderCell(selectedClient.isTutorialComp)}</p>
                            <p><strong>First Canvass Complete:</strong> {renderCell(selectedClient.isFirstCanvassComp)}</p>
                        </Col>
                    </Row>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Confirm Delete"
                footer={
                    <>
                        <AugButton variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                            Cancel
                        </AugButton>
                        <AugButton textColor="var(--brand-as-purple-050)" color="var(--brand-as-purple-700)" onClick={handleDeleteClient} disabled={deletionLoading}>
                            {deletionLoading ? 'Deleting...' : 'Delete'}
                        </AugButton>
                    </>
                }
            >
                <p>Are you sure you want to delete this client?</p>
                {selectedClient && (
                    <p><strong>{selectedClient.displayName || selectedClient.clientId}</strong></p>
                )}
                {deletionError && (
                    <AugAlert type="error" title="Error Alert" text={deletionError} className="mb-0" />
                )}
            </Modal>

            <Modal
                open={showCreateModal}
                onClose={closeCreateModal}
                title="Add Client"
                footer={
                    <>
                        <AugButton variant="outline" onClick={closeCreateModal} disabled={creatingLoading}>
                            Cancel
                        </AugButton>
                        <AugButton
                            variant="primary"
                            onClick={handleCreateClient}
                            disabled={creatingLoading || !(createDisplayName || '').trim()}
                        >
                            {creatingLoading ? 'Creating Client…' : 'Create Client'}
                        </AugButton>
                    </>
                }
            >
                {creatingError && (
                    <AugAlert type="error" title="Error Alert" text={creatingError} className="mb-3" />
                )}

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Display Name</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={createDisplayName}
                                onChange={(e) => setCreateDisplayName(e.target.value)}
                                disabled={creatingLoading}
                                placeholder="e.g. Acme Corp"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Organization</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={createOrganization}
                                onChange={(e) => setCreateOrganization(e.target.value)}
                                disabled={creatingLoading}
                                placeholder="e.g. Acme Holdings"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Industry</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={createIndustry}
                                onChange={(e) => setCreateIndustry(e.target.value)}
                                disabled={creatingLoading}
                                placeholder="e.g. Manufacturing"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group>
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Job Title</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={createJobTitle}
                                onChange={(e) => setCreateJobTitle(e.target.value)}
                                disabled={creatingLoading}
                                placeholder="e.g. Marketing Manager"
                            />
                        </Col>
                    </Row>
                </Form.Group>
            </Modal>

            <Modal
                open={showEditModal}
                onClose={closeEditModal}
                title="Edit Client"
                footer={
                    <>
                        <AugButton variant="outline" onClick={closeEditModal} disabled={editingLoading}>
                            Cancel
                        </AugButton>
                        <AugButton
                            variant="primary"
                            onClick={handleUpdateClient}
                            disabled={editingLoading || !(editDisplayName || '').trim()}
                        >
                            {editingLoading ? 'Updating Client…' : 'Update Client'}
                        </AugButton>
                    </>
                }
            >
                {editingError && (
                    <AugAlert type="error" title="Error Alert" text={editingError} className="mb-3" />
                )}

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Display Name</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={editDisplayName}
                                onChange={(e) => setEditDisplayName(e.target.value)}
                                disabled={editingLoading}
                                placeholder="e.g. Acme Corp"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Organization</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={editOrganization}
                                onChange={(e) => setEditOrganization(e.target.value)}
                                disabled={editingLoading}
                                placeholder="e.g. Acme Holdings"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Industry</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={editIndustry}
                                onChange={(e) => setEditIndustry(e.target.value)}
                                disabled={editingLoading}
                                placeholder="e.g. Manufacturing"
                            />
                        </Col>
                    </Row>
                </Form.Group>

                <Form.Group>
                    <Row className="align-items-center">
                        <Col xs={4}>
                            <Form.Label className="mb-0">Job Title</Form.Label>
                        </Col>
                        <Col xs={8}>
                            <Form.Control
                                value={editJobTitle}
                                onChange={(e) => setEditJobTitle(e.target.value)}
                                disabled={editingLoading}
                                placeholder="e.g. Marketing Manager"
                            />
                        </Col>
                    </Row>
                </Form.Group>
            </Modal>
        </Container>
    );
};

export default ClientsTable;
